// src/components/stylesnap.jsx
// StyleSnap AI — with subscription quota gate.
// Free users get 1 lifetime style try-on. Paid subscribers get unlimited.
// Supports both modal (with onClose) and full page usage.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, collection, addDoc, serverTimestamp, increment } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import toast from 'react-hot-toast';
import { useSubscription } from '../hooks/useSubscription';
import Paywall from './Paywall';

const GOLD = '#B8860B';
const GLASS = 'rgba(0, 0, 0, 0.98)';

export default function StyleSnap({ onClose, initialUrl }) {
  const [step, setStep] = useState('idle'); 
  const [rawUrl, setRawUrl] = useState(initialUrl || '');
  const [croppedUrl, setCroppedUrl] = useState('');
  const [styleName, setStyleName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState('INITIALIZING AI...');
  const [showPaywall, setShowPaywall] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const sub = useSubscription();

  useEffect(() => {
    if (initialUrl) {
      // Check quota before auto-processing from an external URL
      if (!sub.loading && !sub.canStyleTryOn.allowed) {
        setShowPaywall(true);
        return;
      }
      setStep('analyzing');
      startAIProcessing(initialUrl);
    }
  }, [initialUrl, sub.loading]);

  const handleClose = () => {
    // If it's a modal, call onClose. If it's a page, navigate back.
    if (onClose && onClose.toString() !== '() => {}') {
      onClose();
    } else {
      navigate('/dashboard');
    }
  };

  // ── Quota check before accepting upload ────────────────────────────────────
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!sub.canStyleTryOn.allowed) {
      setShowPaywall(true);
      return;
    }

    const url = URL.createObjectURL(file);
    setRawUrl(url);
    setStep('analyzing');
    startAIProcessing(url);
  };

  const startAIProcessing = (url) => {
    const milestones = [
      { t: 800, msg: 'ISOLATING HAIR ARCHITECTURE...' },
      { t: 1600, msg: 'MAPPING FOLLICLE DENSITY...' },
      { t: 2400, msg: 'GENERATING 3D TEXTURE MESH...' }
    ];

    milestones.forEach(m => setTimeout(() => setStatus(m.msg), m.t));
    
    setTimeout(() => {
      const img = new Image();
      img.crossOrigin = "anonymous"; 
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 512; 
        canvas.height = 512;
        const size = Math.min(img.width, img.height);
        ctx.drawImage(img, (img.width - size)/2, (img.height - size)/2, size, size, 0, 0, 512, 512);
        setCroppedUrl(canvas.toDataURL('image/jpeg', 0.92));
        setStep('naming');
      };
      img.onerror = () => {
        toast.error("Image processing failed.");
        setStep('idle');
      };
    }, 3200);
  };

  const saveToLibrary = async () => {
    if (!styleName.trim() || isSaving) return;
    if (!user) { toast.error("Please login first"); return; }

    // Double-check quota before saving
    if (!sub.canStyleTryOn.allowed) {
      setShowPaywall(true);
      return;
    }

    setIsSaving(true);
    try {
      const col = collection(db, 'users', user.uid, 'customStyles');
      await addDoc(col, {
        name: styleName.trim().toUpperCase(),
        image: croppedUrl,
        source: 'StyleSnap AI',
        type: 'biometric_reference',
        createdAt: serverTimestamp(),
      });
      
      await updateDoc(doc(db, 'users', user.uid), { 
        scanCount: increment(1),
        lifetimeStylesUsed: increment(1),
        lastStyleAddedAt: serverTimestamp() 
      });

      toast.success("Style Locked in 3D Vault!");
      setStep('success');
      setTimeout(() => { 
        handleClose();
        navigate('/viewer'); 
      }, 1500);
    } catch (e) { 
      toast.error("Sync Error.");
      setIsSaving(false); 
    }
  };

  // ── Quota status bar (shown in idle step for free users) ────────────────────
  const QuotaNote = () => {
    if (sub.isSuperAdmin || sub.isSubscriptionActive) return null;
    const used = sub.lifetimeStylesUsed || 0;
    const cap = 1; // Free users get 1 lifetime style try-on

    if (used >= cap) {
      return (
        <div style={quotaWarning}>
          <p style={{ color: '#ff4d4d', fontWeight: '700', fontSize: '0.75rem', margin: '0 0 6px' }}>
            🔒 Free try-on used
          </p>
          <p style={{ fontSize: '0.7rem', opacity: 0.8, margin: '0 0 10px' }}>
            Subscribe for unlimited hairstyle try-ons.
          </p>
          <button onClick={() => setShowPaywall(true)} style={upgradeBtn}>
            VIEW PLANS
          </button>
        </div>
      );
    }

    return (
      <div style={quotaOk}>
        <p style={{ color: '#00ff88', fontWeight: '700', fontSize: '0.65rem', margin: 0 }}>
          ✓ {cap - used} free try-on remaining
        </p>
      </div>
    );
  };

  return (
    <div style={fullOverlay}>
      {/* Paywall modal */}
      {showPaywall && (
        <Paywall
          reason="style_exhausted"
          onClose={() => setShowPaywall(false)}
          redirectAfter="/dashboard"
        />
      )}

      <div style={StyleSnapContainer}>
        <button onClick={handleClose} style={closeX}>×</button>

        {/* IDLE — upload prompt */}
        {step === 'idle' && (
          <div style={centerV}>
            <div style={iconBadge}>📸</div>
            <h1 style={goldTitle}>StyleSnap AI</h1>
            <p style={{color: 'rgba(255,255,255,0.6)', marginBottom: '30px'}}>
              Reference a style to project onto your 3D avatar.
            </p>

            <QuotaNote />

            {sub.canStyleTryOn.allowed ? (
              <label style={actionBtn}>
                UPLOAD REFERENCE
                <input type="file" hidden accept="image/*" onChange={handleUpload} />
              </label>
            ) : (
              <button onClick={() => setShowPaywall(true)} style={{ ...actionBtn, cursor: 'pointer' }}>
                🔒 SUBSCRIBE TO UNLOCK
              </button>
            )}
          </div>
        )}

        {/* ANALYZING */}
        {step === 'analyzing' && (
          <div style={centerV}>
            <div style={scannerBox}>
              <img src={rawUrl} style={rawImg} alt="RAW" />
              <div style={scanLine} />
            </div>
            <h3 style={statusText}>{status}</h3>
          </div>
        )}

        {/* NAMING */}
        {step === 'naming' && (
          <div style={centerV}>
            <div style={finalCircle}>
              <img src={croppedUrl} style={{width:'100%', height:'100%', objectFit:'cover'}} alt="AI" />
            </div>
            <input 
              style={premiumInput} 
              placeholder="NAME THIS VIBE" 
              value={styleName} 
              onChange={(e) => setStyleName(e.target.value)} 
              autoFocus 
            />
            <button onClick={saveToLibrary} style={actionBtn}>
              {isSaving ? 'SECURING...' : 'SECURE IN 3D VAULT'}
            </button>
          </div>
        )}

        {/* SUCCESS */}
        {step === 'success' && (
          <div style={centerV}>
            <div style={successCheck}>✓</div>
            <h2 style={goldTitle}>VAULTED</h2>
          </div>
        )}
      </div>
    </div>
  );
}

// STYLES
const fullOverlay = { 
  position: 'fixed', 
  inset: 0, 
  background: 'black', 
  zIndex: 9999, 
  display: 'grid', 
  placeItems: 'center' 
};

const StyleSnapContainer = { 
  background: GLASS, 
  width: '100%', 
  maxWidth: '500px', 
  height: '100vh', 
  textAlign: 'center', 
  padding: '40px', 
  position: 'relative', 
  borderLeft: `1px solid ${GOLD}`, 
  borderRight: `1px solid ${GOLD}`, 
  display: 'flex', 
  flexDirection: 'column', 
  alignItems: 'center' 
};

const goldTitle = { 
  color: GOLD, 
  fontSize: '2rem', 
  marginBottom: '10px', 
  fontWeight: '900', 
  letterSpacing: '2px' 
};

const iconBadge = { 
  fontSize: '4rem', 
  marginBottom: '20px' 
};

const actionBtn = { 
  background: GOLD, 
  color: 'black', 
  padding: '20px 45px', 
  borderRadius: '50px', 
  fontWeight: '900', 
  border: 'none', 
  cursor: 'pointer', 
  display: 'inline-block' 
};

const scannerBox = { 
  width: '280px', 
  height: '280px', 
  margin: '0 auto 40px', 
  position: 'relative', 
  overflow: 'hidden', 
  borderRadius: '30px', 
  border: `2px solid ${GOLD}` 
};

const rawImg = { 
  width: '100%', 
  height: '100%', 
  objectFit: 'cover', 
  opacity: 0.5 
};

const scanLine = { 
  position: 'absolute', 
  width: '100%', 
  height: '3px', 
  background: GOLD, 
  boxShadow: `0 0 15px ${GOLD}` 
};

const statusText = { 
  color: GOLD, 
  letterSpacing: '3px', 
  fontSize: '0.75rem', 
  fontWeight: 'bold' 
};

const premiumInput = { 
  background: 'none', 
  border: 'none', 
  borderBottom: `2px solid ${GOLD}`, 
  color: 'white', 
  fontSize: '1.4rem', 
  textAlign: 'center', 
  outline: 'none', 
  marginBottom: '30px', 
  width: '80%', 
  textTransform: 'uppercase' 
};

const finalCircle = { 
  width: '220px', 
  height: '220px', 
  borderRadius: '50%', 
  overflow: 'hidden', 
  border: `3px solid ${GOLD}`, 
  margin: '0 auto 40px' 
};

const successCheck = { 
  fontSize: '4rem', 
  color: GOLD, 
  marginBottom: '20px' 
};

const closeX = { 
  position: 'absolute', 
  top: '30px', 
  right: '30px', 
  background: 'none', 
  border: 'none', 
  color: 'white', 
  fontSize: '2rem', 
  cursor: 'pointer' 
};

const centerV = { 
  display: 'flex', 
  flexDirection: 'column', 
  justifyContent: 'center', 
  alignItems: 'center', 
  height: '100%', 
  width: '100%' 
};

const quotaWarning = { 
  background: 'rgba(255,77,77,0.1)', 
  border: '1px solid rgba(255,77,77,0.3)', 
  borderRadius: '14px', 
  padding: '16px 20px', 
  marginBottom: '20px', 
  width: '100%', 
  maxWidth: '320px', 
  textAlign: 'center' 
};

const quotaOk = { 
  background: 'rgba(0,255,136,0.08)', 
  border: '1px solid rgba(0,255,136,0.2)', 
  borderRadius: '10px', 
  padding: '10px 18px', 
  marginBottom: '20px' 
};

const upgradeBtn = { 
  background: GOLD, 
  color: '#000', 
  border: 'none', 
  padding: '10px 24px', 
  borderRadius: '50px', 
  fontWeight: '900', 
  cursor: 'pointer', 
  fontSize: '0.8rem' 
};
